/**
 * telegram.api.ts — ручной axios-клиент над /api/v1/messenger-providers/telegram
 * (кодогена для messenger нет — образец zalo.api.ts). DEV-878 / ADR-0030.
 */
import { apiClient } from '@/api'
import type {
  TelegramStatusResponse,
  TelegramStartLoginResponse,
  TelegramSubmitPasswordRequest,
  TelegramSubmitPasswordResponse,
  TelegramSupportSubmitPasswordRequest,
  TelegramLogoutResponse,
  TelegramChatsResponse,
  TelegramClientChatsResponse,
  TelegramMessagesResponse,
  TelegramSendTextRequest,
  TelegramSendImageRequest,
  TelegramSendDocumentRequest,
  TelegramSendMessageResponse,
  TelegramMarkAsReadRequest,
  TelegramReactRequest,
  TelegramBindRequest,
  TelegramActionResponse,
  TelegramCallDetailsResponse,
  TelegramListCallsResponse,
} from '@/features/messenger/types/telegram.types'

const BASE_PATH = '/api/v1/messenger-providers/telegram'

/**
 * DEV-1712. Общий (support) аккаунт компании: те же четыре шага сессии, но
 * поверх своих ручек. Все они гейтятся правом messengers:manage.
 */
const SUPPORT_PATH = `${BASE_PATH}/support`

export const telegramApi = {
  // ---- session ----
  async getStatus(): Promise<TelegramStatusResponse> {
    const response = await apiClient.get<TelegramStatusResponse>(`${BASE_PATH}/status`)
    return response.data
  },

  async startLogin(): Promise<TelegramStartLoginResponse> {
    const response = await apiClient.post<TelegramStartLoginResponse>(`${BASE_PATH}/login`, {})
    return response.data
  },

  async submitPassword(
    body: TelegramSubmitPasswordRequest,
  ): Promise<TelegramSubmitPasswordResponse> {
    const response = await apiClient.post<TelegramSubmitPasswordResponse>(
      `${BASE_PATH}/login/password`,
      body,
    )
    return response.data
  },

  async logout(): Promise<TelegramLogoutResponse> {
    const response = await apiClient.post<TelegramLogoutResponse>(`${BASE_PATH}/logout`, {})
    return response.data
  },

  // ---- chats / messages ----
  // managerId (DEV-998): read-only просмотр чата другого менеджера (ADMIN/CEO/HOS).
  // clientId (DEV-2156): только свои диалоги, привязанные к этому клиенту CRM.
  async getChats(params?: {
    limit?: number
    offset?: number
    search?: string
    managerId?: string
    clientId?: string
  }): Promise<TelegramChatsResponse> {
    // DEV-302: курсорная пагинация (page_token — offset-строка)
    const query = {
      'pagination.pageSize': params?.limit,
      'pagination.pageToken': params?.offset ? String(params.offset) : undefined,
      search: params?.search,
      target_manager_id: params?.managerId,
      client_id: params?.clientId,
    }
    const response = await apiClient.get<TelegramChatsResponse>(`${BASE_PATH}/chats`, {
      params: query,
    })
    return response.data
  },

  // DEV-998: чаты клиента по всем менеджерам (наполняет селектор кросс-просмотра).
  async getClientChats(clientId: string): Promise<TelegramClientChatsResponse> {
    const encoded = encodeURIComponent(clientId)
    const response = await apiClient.get<TelegramClientChatsResponse>(
      `${BASE_PATH}/clients/${encoded}/chats`,
    )
    return response.data
  },

  async getMessages(
    peerKey: string,
    params?: { limit?: number; offset?: number; managerId?: string },
  ): Promise<TelegramMessagesResponse> {
    const encoded = encodeURIComponent(peerKey)
    const query = {
      'pagination.pageSize': params?.limit,
      'pagination.pageToken': params?.offset ? String(params.offset) : undefined,
      target_manager_id: params?.managerId,
    }
    const response = await apiClient.get<TelegramMessagesResponse>(
      `${BASE_PATH}/chats/${encoded}/messages`,
      { params: query },
    )
    return response.data
  },

  // ---- send ----
  async sendText(body: TelegramSendTextRequest): Promise<TelegramSendMessageResponse> {
    const response = await apiClient.post<TelegramSendMessageResponse>(
      `${BASE_PATH}/messages/text`,
      body,
    )
    return response.data
  },

  async sendImage(body: TelegramSendImageRequest): Promise<TelegramSendMessageResponse> {
    const response = await apiClient.post<TelegramSendMessageResponse>(
      `${BASE_PATH}/messages/image`,
      body,
    )
    return response.data
  },

  async sendDocument(body: TelegramSendDocumentRequest): Promise<TelegramSendMessageResponse> {
    const response = await apiClient.post<TelegramSendMessageResponse>(
      `${BASE_PATH}/messages/document`,
      body,
    )
    return response.data
  },

  async markAsRead(body: TelegramMarkAsReadRequest): Promise<TelegramActionResponse> {
    const response = await apiClient.post<TelegramActionResponse>(
      `${BASE_PATH}/messages/read`,
      body,
    )
    return response.data
  },

  async react(body: TelegramReactRequest): Promise<TelegramActionResponse> {
    const response = await apiClient.post<TelegramActionResponse>(
      `${BASE_PATH}/messages/react`,
      body,
    )
    return response.data
  },

  // ---- client binding ----
  async bindChatToClient(body: TelegramBindRequest): Promise<TelegramActionResponse> {
    const encoded = encodeURIComponent(body.peer_key)
    const response = await apiClient.post<TelegramActionResponse>(
      `${BASE_PATH}/chats/${encoded}/bind`,
      { client_id: body.client_id },
    )
    return response.data
  },

  // ---- calls (DEV-925) ----
  // managerId (DEV-998): read-only детали звонка другого менеджера (ADMIN/CEO/HOS).
  async getCallDetails(callId: string, managerId?: string): Promise<TelegramCallDetailsResponse> {
    const encoded = encodeURIComponent(callId)
    const response = await apiClient.get<TelegramCallDetailsResponse>(
      `${BASE_PATH}/calls/${encoded}/details`,
      { params: { target_manager_id: managerId } },
    )
    return response.data
  },

  // ---- calls history (DEV-982) ----
  // managerId (DEV-998): read-only список звонков другого менеджера (ADMIN/CEO/HOS).
  async listCalls(
    clientId: string,
    limit?: number,
    managerId?: string,
  ): Promise<TelegramListCallsResponse> {
    const response = await apiClient.get<TelegramListCallsResponse>(`${BASE_PATH}/calls`, {
      params: { client_id: clientId, limit, target_manager_id: managerId },
    })
    return response.data
  },
}

/**
 * Сессия общего аккаунта поддержки (DEV-1712). Чатов здесь нет намеренно:
 * переписка общего аккаунта — отдельная задача (DEV-1833).
 */
export const telegramSupportApi = {
  async getStatus(): Promise<TelegramStatusResponse> {
    const response = await apiClient.get<TelegramStatusResponse>(`${SUPPORT_PATH}/status`)
    return response.data
  },

  async startLogin(): Promise<TelegramStartLoginResponse> {
    const response = await apiClient.post<TelegramStartLoginResponse>(`${SUPPORT_PATH}/login`, {})
    return response.data
  },

  async submitPassword(
    body: TelegramSupportSubmitPasswordRequest,
  ): Promise<TelegramSubmitPasswordResponse> {
    const response = await apiClient.post<TelegramSubmitPasswordResponse>(
      `${SUPPORT_PATH}/login/password`,
      body,
    )
    return response.data
  },

  async logout(): Promise<TelegramLogoutResponse> {
    const response = await apiClient.post<TelegramLogoutResponse>(`${SUPPORT_PATH}/logout`, {})
    return response.data
  },
}
